import React, { Fragment, FunctionComponent, memo } from 'react'
import Collapse from 'blocks.simple/collapse/collapse'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { grid } from 'theme'
import { IAuthenticationSettingsComponent } from './AuthenticationSettings-types'
import { ConfirmEmailDialog } from 'organisms/ConfirmEmailDialog'
import { Button } from 'molecules/Button'
import { useConfirmEmailCode, useConfirmationEmail } from './AuthenticationSettings-hooks'
import { authenticationSettingsStyles } from './AuthenticationSettings-styles'
import { useLocation } from 'react-router'
import { getURLSearchParamsByLocation } from 'features/routes/utils'
import { routes } from 'features/routes'
import styles from '../settings.jcss'
import { TwoFactorSettings } from './TwoFactorSettings-view'

const maskEmail = (email?: string) => {
    if (!email || !email.includes('@')) {
        return undefined
    }
    const [local, domain] = email.split('@')
    if (local.length <= 3) {
        return `${local[0] || ''}•••@${domain}`
    }
    return `${local.slice(0, 4)}•••@${domain}`
}

const AuthenticationSettingsComponent: FunctionComponent<IAuthenticationSettingsComponent> = ({
    twoFactorAuth = 'off',
    isEmailConfirmed = false,
    totpEnabled = false,
    email,
}) => {
    const location = useLocation()
    const query = getURLSearchParamsByLocation(location)
    const confirmEmailCode = query[`${routes.confirmEmail.prefix}_code`] || null

    const { changeCode, code } = useConfirmEmailCode()

    const {
        prefix,
        isSendingEmail,
        isModalOpen,
        isLocalEmailConfirmed,
        resendLeft,
        closeModal,
        status,
        sendConfirmationEmail,
        sendConfirmationEmailCode,
    } = useConfirmationEmail(confirmEmailCode)

    const isEmailConfirmedSuccessfully = isEmailConfirmed || isLocalEmailConfirmed

    const handleConfirmEmailDialogClose = () => {
        closeModal()
    }

    const handleSendConfirmationEmail = () => {
        if (isSendingEmail) {
            return
        }
        sendConfirmationEmail()
    }

    const handleRepeatConfirmationEmail = () => {
        closeModal()
        sendConfirmationEmail()
    }

    const renderConfirmEmailDialogContent = () => {
        switch (status) {
            case 'success':
                return (
                    <div className={authenticationSettingsStyles.authenticationSettings__subText}>
                        {translate('confirmEmailSuccessTitle')}
                    </div>
                )
            case 'error':
                return (
                    <div className={authenticationSettingsStyles.authenticationSettings__inner}>
                        <Button size="m" mod="fillBrand" onClick={handleRepeatConfirmationEmail}>
                            {translate('send')}
                        </Button>
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <Fragment>
            <Collapse
                title={() => translate('twoFactorAuth')}
                className={cn(
                    grid.mb_md,
                    styles.background,
                    authenticationSettingsStyles.authenticationSettings,
                    'test-settings-item'
                )}
            >
                <TwoFactorSettings
                    twoFactorAuth={twoFactorAuth}
                    isEmailConfirmed={isEmailConfirmedSuccessfully}
                    totpEnabled={totpEnabled}
                    maskedEmail={maskEmail(email)}
                    emailConfirm={{
                        prefix,
                        resendLeft,
                        isSendingEmail,
                        code,
                        onChangeCode: changeCode,
                        onSendConfirmationEmail: handleSendConfirmationEmail,
                        onSendConfirmationEmailCode: sendConfirmationEmailCode,
                    }}
                />
            </Collapse>

            <ConfirmEmailDialog open={isModalOpen} status={status} onClose={handleConfirmEmailDialogClose}>
                {renderConfirmEmailDialogContent()}
            </ConfirmEmailDialog>
        </Fragment>
    )
}

export const AuthenticationSettings = memo(AuthenticationSettingsComponent)
